import { Link } from "@tanstack/react-router";
import { PageHeader, Section, Table, TBody, TD, TDNum, TH, THead, TR, formatCount } from "@ghezaa/ui";
import type { GapCustomer } from "@/api/client";
import { useDataGaps } from "@/api/queries";
import { inCurrency, money, percent } from "@/lib/format";
import { useCurrentMarket } from "@/lib/market";

/**
 * What Business Central lacks for these figures this year, for Sales to put right in BC: Sales never patches it. Each
 * gap is fixed on the BC record named (a document's salesperson or business unit, a customer's salesperson code) or on
 * the People page (a code nobody is linked to).
 */
export function DataGapsPage() {
  const market = useCurrentMarket();
  const gaps = useDataGaps(market.code);
  const d = gaps.data;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Data gaps" description="What Business Central lacks for these figures this year, to put right in BC." />
      {gaps.isError && <p className="bg-danger-soft px-3 py-2 text-sm text-danger">Could not load the gaps: {gaps.error.message}</p>}
      {!d && !gaps.isError && <div className="h-40 bg-surface-2" aria-busy="true" />}
      {d && (
        <div className="flex flex-col">
          <Section n="01" title="No business unit" note="Revenue on documents without BUSINESSUNIT: the team's figures cannot be split by unit." wide>
            <div className="flex flex-col gap-3">
              <p className="text-sm text-ink-2">
                {money(d.revenueNoBusinessUnit)} {market.currency} of {money(d.revenue)}, {percent(d.revenue ? d.revenueNoBusinessUnit / d.revenue : null)}, on{" "}
                {formatCount(d.documentsNoBusinessUnit)} documents.
              </p>
              <Customers rows={d.noBusinessUnit} currency={market.currency} empty="Every document this year has a business unit." />
            </div>
          </Section>
          <Section n="02" title="No salesperson on the document" note="Counted under No salesperson on the Team page." wide>
            <p className="text-sm text-ink-2">
              {d.documentsNoSalesperson === 0
                ? "Every document this year names a salesperson."
                : `${money(d.revenueNoSalesperson)} ${market.currency} on ${formatCount(d.documentsNoSalesperson)} documents.`}
            </p>
          </Section>
          <Section n="03" title="Customers without a salesperson" note="They bought this year and are in nobody's book." wide>
            <Customers rows={d.customersNoSalesperson} currency={market.currency} empty="Every customer who bought this year has a salesperson code." />
          </Section>
          <Section n="04" title="Customers on a code that is no one" note="On a code marked no one on the People page, such as STAFF." wide>
            <Customers rows={d.customersOnNoOne} currency={market.currency} empty="No customer who bought this year is on such a code." withCode />
          </Section>
          <Section n="05" title="Codes nobody is linked to" note="Their customers are in no one's own book until a person is linked." wide>
            {d.codesNotLinked.length === 0 ? (
              <p className="text-sm text-ink-3">Every code with customers is linked, or marked no one.</p>
            ) : (
              <Table density="compact">
                <THead>
                  <TR>
                    <TH>Code</TH>
                    <TH className="text-end">Customers</TH>
                  </TR>
                </THead>
                <TBody>
                  {d.codesNotLinked.map((c) => (
                    <TR key={c.code} linked>
                      <TD className="w-full max-w-0">
                        <Link from="/$market" to="/$market/people" search={{ open: c.code } as never} className="font-mono text-xs underline-offset-2 hover:underline">
                          {c.code}
                        </Link>
                        {c.name && <span className="block truncate text-xs text-ink-3">{c.name}</span>}
                      </TD>
                      <TDNum>{formatCount(c.customers)}</TDNum>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </Section>
        </div>
      )}
    </div>
  );
}

function Customers({ rows, currency, empty, withCode = false }: { rows: GapCustomer[]; currency: string; empty: string; withCode?: boolean }) {
  if (rows.length === 0) return <p className="text-sm text-ink-3">{empty}</p>;
  return (
    <Table density="compact">
      <THead>
        <TR>
          <TH>Customer</TH>
          {withCode && <TH from="md">Code</TH>}
          <TH className="text-end whitespace-nowrap">{inCurrency("This year", currency)}</TH>
        </TR>
      </THead>
      <TBody>
        {rows.map((c) => (
          <TR key={c.no} linked>
            <TD className="w-full max-w-0">
              <Link from="/$market" to="/$market/customers/$no" params={{ no: c.no }} className="block truncate underline-offset-2 hover:underline">
                <bdi>{c.name || c.no}</bdi>
              </Link>
              <span className="block text-xs text-ink-3">
                <span className="font-mono">{c.no}</span>
              </span>
            </TD>
            {withCode && <TD from="md" className="whitespace-nowrap font-mono text-xs">{c.salespersonCode}</TD>}
            <TDNum>{money(c.revenue)}</TDNum>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}
